import type {
  DanteMotionPreference,
  DantePalette,
  DanteRenderParameters,
  DanteStateId,
  DanteView,
} from "@dante/core";
import {
  applyDanteRenderParameters,
  blendDantePose,
  DANTE_RIG_LAYERS,
  DANTE_STATUS_SYMBOLS,
  dantePartTransform,
  danteStageTransform,
  danteState,
  organicAvatarFamily,
  sampleDantePose,
} from "@dante/core";
import type { DantePaletteColors } from "@dante/ui-tokens";
import { blendDantePalette, dantePalettes } from "@dante/ui-tokens";
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import profileUrl from "../../ui-tokens/assets/character/dante-profile.webp?url";
import { cn } from "./lib/utils.js";

export interface DanteCharacterProps extends DanteRenderParameters {
  portrait?: boolean;
  state?: DanteStateId;
  taskId?: string;
  taskName?: string;
  detail?: string;
  size?: number;
  palette?: DantePalette;
  view?: DanteView;
  motion?: DanteMotionPreference;
  showLabel?: boolean;
  className?: string;
  identityColor?: string;
  identity?: string;
}

function useDanteColors(palette: DantePalette | undefined, settled: boolean, reduced: boolean) {
  const [colors, setColors] = useState<DantePaletteColors | null>(
    palette ? dantePalettes[palette] : null,
  );
  const current = useRef(colors);
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const apply = () => {
      if (!settled) return;
      if (timer) clearInterval(timer);
      const target =
        dantePalettes[
          palette ?? (document.documentElement.dataset.theme === "light" ? "field" : "watch")
        ];
      const from = current.current;
      const save = (next: DantePaletteColors) => {
        current.current = next;
        setColors(next);
      };
      if (
        !from ||
        reduced ||
        Object.keys(target).every(
          (key) =>
            from[key as keyof DantePaletteColors] === target[key as keyof DantePaletteColors],
        )
      ) {
        save(target);
        return;
      }
      const start = performance.now();
      timer = setInterval(() => {
        const progress = (performance.now() - start) / 800;
        save(progress >= 1 ? target : blendDantePalette(from, target, progress));
        if (progress >= 1 && timer) clearInterval(timer);
      }, 1000 / 24);
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => {
      if (timer) clearInterval(timer);
      observer.disconnect();
    };
  }, [palette, settled, reduced]);
  return colors;
}

/** One foreground task drives the character. Animation completion has no application callback. */
export function DanteCharacter({
  state = "quiet-watch",
  portrait = false,
  taskId,
  taskName = "Dante",
  detail,
  size = 96,
  palette,
  view = "front-left",
  motion = "full",
  showLabel = false,
  className,
  identityColor,
  identity,
  gazeTarget,
  stagePosition,
  expressiveAmplitude,
}: DanteCharacterProps) {
  const root = useRef<HTMLSpanElement>(null);
  const [elapsed, setElapsed] = useState(0);
  const [reduced, setReduced] = useState(true);
  const semantic = danteState(state);
  const settled =
    reduced ||
    motion !== "full" ||
    portrait ||
    size < 48 ||
    elapsed >= (semantic.playback === "loop" ? 220 : semantic.durationMs);
  const colors = useDanteColors(palette, settled, reduced || motion !== "full");
  const previous = useRef({
    key: `${state}:${taskId}`,
    pose: sampleDantePose(state, 0, "still"),
    from: sampleDantePose(state, 0, "still"),
  });
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    setElapsed(0);
    if (
      reduced ||
      motion !== "full" ||
      portrait ||
      size < 48 ||
      typeof requestAnimationFrame !== "function"
    )
      return;
    let frame = 0;
    let visible = true;
    let last = 0;
    let time = 0;
    let previous = 0;
    const tick = (now: number) => {
      if (previous && visible && !document.hidden) time += Math.min(now - previous, 100);
      previous = now;
      if (visible && !document.hidden && now - last >= 1000 / 24) {
        setElapsed(time);
        last = now;
      }
      frame = requestAnimationFrame(tick);
    };
    const observer =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(([entry]) => {
            visible = entry?.isIntersecting ?? false;
          });
    if (root.current) observer?.observe(root.current);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [state, taskId, motion, reduced, size, portrait]);
  const key = `${state}:${taskId}`;
  const changed = previous.current.key !== key;
  if (changed) previous.current = { key, pose: previous.current.pose, from: previous.current.pose };
  const still = reduced || portrait || size < 48 || motion !== "full";
  const sampled = sampleDantePose(state, changed ? 0 : elapsed, still ? "still" : "full");
  const animated = still
    ? sampled
    : blendDantePose(previous.current.from, sampled, changed ? 0 : elapsed);
  previous.current.pose = animated;
  const pose = applyDanteRenderParameters(animated, { gazeTarget, expressiveAmplitude });
  const paletteStyle = colors
    ? (Object.fromEntries(
        Object.entries(colors).map(([name, color]) => [
          `--dante-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`,
          color,
        ]),
      ) as CSSProperties)
    : undefined;
  const symbol = DANTE_STATUS_SYMBOLS[semantic.status];
  const label = `${taskName}: ${detail || semantic.label}`;
  return (
    <span
      ref={root}
      className={cn(
        "dante-character inline-flex shrink-0 items-center gap-2",
        portrait && "dante-organic-avatar",
        className,
      )}
      data-dante-state={state}
      data-dante-palette={palette}
      data-task-id={taskId}
      data-identity={identity}
      data-shape-family={identity ? organicAvatarFamily(identity) : undefined}
      style={paletteStyle}
      data-motion={reduced ? "reduced" : motion}
      role="img"
      aria-live={showLabel ? "polite" : "off"}
      aria-label={label}
    >
      <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
        <svg
          style={portrait || size < 48 ? { borderRadius: "16%", overflow: "hidden" } : undefined}
          width={size}
          height={size}
          viewBox="0 0 512 512"
          fill="none"
          aria-hidden="true"
          strokeLinejoin="round"
          strokeLinecap="round"
        >
          {portrait || size < 48 ? (
            <image
              data-dante-portrait="true"
              href={profileUrl}
              width={512}
              height={512}
              preserveAspectRatio="xMidYMid meet"
            />
          ) : (
            <g transform={danteStageTransform(stagePosition)}>
              {DANTE_RIG_LAYERS.map((layer) => (
                <path
                  key={layer.id}
                  data-material={layer.material}
                  data-part={layer.id}
                  d={layer.path}
                  transform={dantePartTransform(layer.part, pose, view)}
                  fill={`var(--dante-${layer.material.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)})`}
                  stroke={layer.stroke ? `var(--dante-${layer.stroke})` : undefined}
                  strokeWidth={layer.strokeWidth}
                />
              ))}
            </g>
          )}
        </svg>
        {identityColor && (
          <span
            aria-hidden="true"
            className="absolute bottom-0 left-0 h-0.5 w-2 rounded-full"
            style={{ backgroundColor: identityColor }}
          />
        )}
        {symbol && (
          <span
            aria-hidden="true"
            className="dante-character-status absolute bottom-0 right-0 rounded-sm bg-background font-mono leading-none"
            style={
              {
                color: `var(--dante-${semantic.status})`,
                fontSize: Math.max(12, Math.min(18, size / 3)),
              } as CSSProperties
            }
          >
            {symbol}
          </span>
        )}
      </span>
      {showLabel && (
        <span aria-hidden="true" className="text-sm text-foreground">
          {detail || semantic.label}
        </span>
      )}
    </span>
  );
}
